import { useEffect, useState, useMemo } from "react";
import { Linking, Platform } from "react-native";
import { useSignIn } from "@apis/user";
import { useDispatch, useSelector } from "react-redux";
import { setIsGuest, setUserInfo } from "@redux/slices/userInfoSlice";
import { useOrgSettings } from "@apis/orgSettings";
import { useNavigationMenu } from "@apis/navigationMenu";
import ApiRoutes from "@apis/routes";
import { ThemeTokenTypes } from "@scenes/splash-screen/types";

const useGuestUser = () => {
  const [checkFinished, setCheckFinished] = useState(false);
  const signIn = useSignIn();
  const [isNavigationApiEnabled, setIsNavigationApiEnabled] = useState(false);
  const [navigationUrl, setNavigationUrl] = useState(ApiRoutes.NAVIGATION_MENU);
  const installationIdentifier = useSelector(
    (state) => state.appState?.installationIdentifier
  );
  const userInfo = useSelector((state: any) => state.userInfo);
  const dispatch = useDispatch();
  const PLATFORM = Platform.OS === "web" ? 0 : 1;
  const orgSettings = useOrgSettings(PLATFORM);
  useNavigationMenu(
    navigationUrl,
    isNavigationApiEnabled,
    ThemeTokenTypes.COMPANY
  );
  const ssoAuthRef =
    Platform.OS === "web"
      ? new URL(window.location.href).searchParams.get("sso_auth_ref")
      : null;

  const isUserObject = useMemo(
    () => !userInfo.user || Object.keys(userInfo.user).length === 0,
    [userInfo]
  );

  const authenticateGuest = async (
    authData:
      | { iframe_token: string }
      | { public_token: string }
      | { auth_id: string }
      | { sso_token: string }
  ) => {
    try {
      const data = await signIn.mutateAsync({
        ...authData,
        installation_identifier: installationIdentifier,
      });

      if (!data?.data?.user) {
        throw new Error("Invalid user data received");
      }

      const user = data.data.user;
      dispatch(setUserInfo(user));

      dispatch(setIsGuest("public_token" in authData));
      if (Platform.OS === "web") {
        localStorage.setItem("userData", JSON.stringify(user));
        localStorage.setItem("installation_identifier", installationIdentifier);
        localStorage.setItem("token", user.token);
      }

      if (Platform.OS !== "web" && user.json_navigation_menus) {
        setNavigationUrl(user.json_navigation_menus);
        setIsNavigationApiEnabled(true);
      }
    } catch (error) {
      console.error("Guest authentication failed:", error);
    } finally {
      setCheckFinished(true); // Ensure state update only once
    }
  };

  useEffect(() => {
    const checkGuestLogic = async () => {
      try {
        const initialUrl = await Linking.getInitialURL();
        const organization = orgSettings.data?.data?.organization;

        if (!organization) {
          setCheckFinished(true);
          return;
        }

        const params =
          Platform.OS === "web"
            ? new URLSearchParams(window.location.search)
            : initialUrl
            ? new URLSearchParams(initialUrl.split("?")[1] || "")
            : null;
        const authId = params?.get("auth_id");
        if (authId) {
          await authenticateGuest({ auth_id: authId });
          return;
        }
        if (ssoAuthRef) {
          await authenticateGuest({ sso_token: ssoAuthRef });
          return;
        }
        const iframeKey = params?.get("iframe_key");
        if (iframeKey) {
          await authenticateGuest({ iframe_token: iframeKey });
          return;
        }
        if (
          (Platform.OS === "web" ? initialUrl : true) &&
          organization.settings?.allow_public_login
        ) {
          const isGuest = params?.get("isGuest");

          if (
            (isGuest === "true" ||
              (organization.settings?.enable_external_landing_page &&
                Platform.OS !== "web")) &&
            isUserObject &&
            organization.settings?.public_key
          ) {
            await authenticateGuest({
              public_token: organization.settings.public_key,
            });
            return;
          }
        }
      } catch (error) {
        console.error("Error in checkGuestLogic:", error);
      } finally {
        setCheckFinished(true);
      }
    };

    if (orgSettings.isSuccess) {
      checkGuestLogic();
    }
  }, [orgSettings.isSuccess, isUserObject]);

  return { checkFinished };
};

export default useGuestUser;
